import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@datapay-by-datacom/design-system";
import { useOrgParams, usePermissions } from "../hooks";
import { useUploadQueue } from "../hooks/OrgDocument/useUploadQueue";
import { OrgView } from "../components/Org";
import { SourceTable } from "../components/OrgSource/SourceTable";
import { CreateSourceModal } from "../components/OrgSource/CreateSourceModal";
import { OrgDocumentUploadStagingArea } from "../components/OrgDocument/OrgDocumentUploadStagingArea";
import { UnauthorizedMessage } from "../../shared/components";
import { SourceOrgCategory } from "../../generated/ingest-api";
import styles from "./OrgSourcesScreen.module.css";

export function OrgSourcesScreen() {
  const { category, id } = useOrgParams();
  const { canViewOrg, isAdminOfOrg } = usePermissions();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const queryClient = useQueryClient();

  const {
    queue,
    addFiles,
    processQueue,
    removeQueueItem,
    isProcessing,
    setQueue,
  } = useUploadQueue({
    orgId: id,
    orgCategory: category as "public" | "internal" | "personal",
    onComplete: () => {
      queryClient.invalidateQueries({ queryKey: ["listSources", category, id] });
      queryClient.invalidateQueries({
        queryKey: ["listDocumentsByOrg", category, id],
      });
      setQueue([]);
    },
  });

  if (!canViewOrg(id, category)) {
    return <UnauthorizedMessage />;
  }

  return (
    <OrgView title="Documents">
      {() => (
        <>
          {isAdminOfOrg(id) && (
            <>
              <div className={styles.section}>
                <div className={styles.sectionLabel}>Manual Uploads:</div>
                <OrgDocumentUploadStagingArea
                  queue={queue}
                  isProcessing={isProcessing}
                  processQueue={processQueue}
                  removeQueueItem={removeQueueItem}
                  addFiles={addFiles}
                />
              </div>
              <div className={styles.section}>
                <div className={styles.sectionLabel}>NZ Legislation:</div>
                <Button
                  variant="primary"
                  onPress={() => setIsCreateOpen(true)}
                >
                  Add Document
                </Button>
                <CreateSourceModal
                  category={category as SourceOrgCategory}
                  orgId={id}
                  isOpen={isCreateOpen}
                  onClose={() => setIsCreateOpen(false)}
                />
              </div>
            </>
          )}
          <SourceTable
            category={category as SourceOrgCategory}
            orgId={id}
          />
        </>
      )}
    </OrgView>
  );
}
